import React, { useEffect, useState } from "react";
import { Building2, Home, HeartPulse, Stethoscope, Package, Truck, FileText, HandHeart, Check, Loader2, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-muted/50 border border-border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary/50 outline-none";

const DEFAULTS = {
  pro_eyebrow: "Espace Professionnels",
  pro_title: "Passerelle pour les établissements et les professionnels du soin",
  pro_subtitle: "EHPAD, foyers de vie, hôpitaux, ergothérapeutes et libéraux : équipez vos unités d'objets qui prennent soin, fabriqués à la main et adaptés à vos publics.",
  pro_price_title: "Des tarifs dégressifs, un devis pour chaque projet",
  pro_price_text: "Chaque structure est unique : nos tarifs sont dégressifs selon les quantités, et chaque projet fait l'objet d'un devis personnalisé, gratuit et sans engagement.",
  pro_price_1_t: "", pro_price_1_d: "",
  pro_price_2_t: "", pro_price_2_d: "",
  pro_price_3_t: "", pro_price_3_d: "",
};

const POUR_QUI = [
  { icon: Building2, t: "EHPAD & résidences", d: "Unités Alzheimer et espaces de vie : objets sensoriels, boîtes à souvenirs, repères du quotidien." },
  { icon: Home, t: "Foyers de vie & IME", d: "Des créations adaptées aux troubles du spectre autistique et aux besoins sensoriels particuliers." },
  { icon: HeartPulse, t: "Hôpitaux & cliniques", d: "Services gériatrie et soins de suite : objets d'apaisement et de stimulation douce." },
  { icon: Stethoscope, t: "Libéraux & ergothérapeutes", d: "Des outils d'accompagnement non médicamenteux à proposer dans vos suivis." },
];

const INCLUS = [
  { icon: Package, t: "Quantités adaptées", d: "De quelques pièces à l'équipement complet d'une unité." },
  { icon: HandHeart, t: "Choix accompagné", d: "Nous vous aidons à composer la sélection selon vos publics." },
  { icon: Truck, t: "Livraison suivie", d: "Expédition soignée et suivi jusqu'à réception dans vos locaux." },
  { icon: FileText, t: "Facturation établissement", d: "Devis, facture et documents administratifs pour votre comptabilité." },
];

const ORG_TYPES = ["EHPAD / résidence", "Foyer de vie / IME", "Hôpital / clinique", "Ergothérapeute / libéral", "Association", "Autre structure"];

const Professionnels = () => {
  const [c, setC] = useState(DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    org_type: ORG_TYPES[0], org_name: "", contact_name: "", email: "",
    phone: "", residents_count: "", products: "", quantities: "", message: "",
  });

  useEffect(() => { api.get("/content").then((r) => setC({ ...DEFAULTS, ...r.data })).catch(() => {}); }, []);

  const tiers = [1, 2, 3]
    .map((i) => ({ t: c[`pro_price_${i}_t`], d: c[`pro_price_${i}_d`] }))
    .filter((t) => t.t && t.d);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/pro/quote", form);
      setDone(true);
      toast.success("Votre demande de devis a bien été envoyée !");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="pt-32 pb-4" data-testid="professionnels-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="text-sm uppercase tracking-[0.3em] text-primary mb-5">{c.pro_eyebrow}</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light max-w-4xl leading-tight">{c.pro_title}</h1>
          <p className="mt-6 text-lg text-foreground/70 max-w-2xl leading-relaxed">{c.pro_subtitle}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-12 rounded-[2.5rem] overflow-hidden border border-warm/25 shadow-[0_20px_60px_rgb(0,0,0,0.08)] grain" data-testid="pro-banner">
            <img src="/affiche-professionnels.jpg" alt="Une soignante et une résidente partagent un moment tendre autour d'objets réconfortants, veillées par une gardienne" className="w-full h-auto block" />
          </div>
        </Reveal>
      </section>

      {/* POUR QUI */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {POUR_QUI.map(({ icon: Icon, t, d }, i) => (
            <Reveal key={t} delay={i * 0.08}>
              <div className="bg-card rounded-3xl border border-border/50 p-7 h-full">
                <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-secondary-foreground" strokeWidth={1.6} />
                </div>
                <h3 className="font-serif-display text-xl mt-5">{t}</h3>
                <p className="mt-2 text-sm text-foreground/60 leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TARIFS */}
      <section className="bg-accent/30 py-20" data-testid="pro-pricing">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.3em] text-warm mb-5">Tarifs établissements</p>
            <h2 className="font-serif-display italic text-4xl sm:text-5xl font-light leading-tight max-w-3xl">{c.pro_price_title}</h2>
            <p className="mt-6 text-lg text-foreground/70 leading-relaxed max-w-2xl">{c.pro_price_text}</p>
          </Reveal>
          {tiers.length > 0 && (
            <div className="grid sm:grid-cols-3 gap-6 mt-12">
              {tiers.map((t) => (
                <div key={t.t} className="bg-card rounded-3xl border border-warm/30 p-7" data-testid={`pro-tier-${t.t.slice(0, 12)}`}>
                  <h3 className="font-serif-display text-xl">{t.t}</h3>
                  <p className="mt-2 text-sm text-foreground/60 leading-relaxed">{t.d}</p>
                </div>
              ))}
            </div>
          )}
          <Reveal delay={0.1}>
            <p className="mt-10 text-sm text-foreground/55 max-w-2xl leading-relaxed">
              À noter : l'accès illimité à l'application Passerelle offert dès 100 € d'achat est une offre réservée aux particuliers.
              Pour les établissements, les conditions d'accès à l'application sont définies dans votre devis.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CE QUI EST INCLUS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {INCLUS.map(({ icon: Icon, t, d }, i) => (
            <Reveal key={t} delay={i * 0.08}>
              <div className="flex gap-4">
                <div className="shrink-0 w-11 h-11 rounded-full bg-warm/15 border border-warm/30 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-warm" strokeWidth={1.7} />
                </div>
                <div>
                  <h3 className="font-serif-display text-lg">{t}</h3>
                  <p className="mt-1 text-sm text-foreground/60 leading-relaxed">{d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FORMULAIRE DEVIS */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-24">
        <Reveal>
          <p className="text-sm uppercase tracking-[0.3em] text-primary mb-4">Devis personnalisé</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-light">Parlez-nous de votre structure</h2>
          <p className="mt-4 text-foreground/70 leading-relaxed">Réponse personnelle sous 48h ouvrées — devis gratuit et sans engagement.</p>
        </Reveal>
        {done ? (
          <div className="mt-10 bg-secondary/20 border border-secondary/40 rounded-3xl p-10 text-center" data-testid="pro-quote-success">
            <div className="w-14 h-14 rounded-full bg-secondary/40 flex items-center justify-center mx-auto"><Check className="w-7 h-7 text-secondary-foreground" /></div>
            <h3 className="font-serif-display text-2xl mt-5">Merci pour votre demande</h3>
            <p className="text-foreground/70 mt-3">Votre demande de devis nous est parvenue. Nous revenons vers vous très vite.</p>
            <button onClick={() => { setDone(false); setForm({ org_type: ORG_TYPES[0], org_name: "", contact_name: "", email: "", phone: "", residents_count: "", products: "", quantities: "", message: "" }); }} className="mt-6 text-primary underline" data-testid="pro-quote-again">Envoyer une autre demande</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="pro-quote-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.12em] text-warm">Type d'établissement *</span>
                <select value={form.org_type} onChange={set("org_type")} className={`mt-1.5 ${inputCls}`} data-testid="pro-quote-org-type">
                  {ORG_TYPES.map((o) => <option key={o}>{o}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.12em] text-warm">Nom de la structure *</span>
                <input required value={form.org_name} onChange={set("org_name")} className={`mt-1.5 ${inputCls}`} placeholder="EHPAD Les Tilleuls" data-testid="pro-quote-org-name" />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.12em] text-warm">Nom du contact *</span>
                <input required value={form.contact_name} onChange={set("contact_name")} className={`mt-1.5 ${inputCls}`} placeholder="Marie Dupont" data-testid="pro-quote-contact-name" />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.12em] text-warm">Email professionnel *</span>
                <input required type="email" value={form.email} onChange={set("email")} className={`mt-1.5 ${inputCls}`} placeholder="contact@etablissement.fr" data-testid="pro-quote-email" />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.12em] text-warm">Téléphone</span>
                <input value={form.phone} onChange={set("phone")} className={`mt-1.5 ${inputCls}`} placeholder="06 12 34 56 78" data-testid="pro-quote-phone" />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.12em] text-warm">Résidents / bénéficiaires concernés</span>
                <input value={form.residents_count} onChange={set("residents_count")} className={`mt-1.5 ${inputCls}`} placeholder="ex. 45 résidents" data-testid="pro-quote-residents" />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.12em] text-warm">Produits visés</span>
                <input value={form.products} onChange={set("products")} className={`mt-1.5 ${inputCls}`} placeholder="ex. plaids lestés, boîtes à souvenirs…" data-testid="pro-quote-products" />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.12em] text-warm">Quantités estimées</span>
                <input value={form.quantities} onChange={set("quantities")} className={`mt-1.5 ${inputCls}`} placeholder="ex. 20 à 30 pièces" data-testid="pro-quote-quantities" />
              </label>
            </div>
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.12em] text-warm">Votre besoin</span>
              <textarea rows={4} value={form.message} onChange={set("message")} className={`mt-1.5 ${inputCls}`} placeholder="Décrivez votre public, vos contraintes, votre calendrier…" data-testid="pro-quote-message" />
            </label>
            <button type="submit" disabled={loading} data-testid="pro-quote-submit"
              className="rounded-full bg-primary text-primary-foreground px-8 py-4 hover:scale-105 transition-transform duration-300 flex items-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Envoyer ma demande de devis
            </button>
          </form>
        )}
      </section>
    </div>
  );
};

export default Professionnels;
